import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import { useLocation } from 'react-router-dom';
import { useRecogerURLParamsFiltros } from './../../functions/parametrosURL/funcionesParamsURL';


import {
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  TableHead,
  TableSortLabel,
  Typography,
} from "@mui/material";
import Iconify from "../../utilities/iconify";

import "./DataTable.css"
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const getDefaultHeadCells = (t) => [
  { id: "fullName", numeric: false, disablePadding: false, label: t('datatable.propertyManager') },
  { id: "department", numeric: false, disablePadding: false, label: t('datatable.community') },
  { id: "medSeg", numeric: false, disablePadding: false, label: t('datatable.nif') },
  { id: "email", numeric: true, disablePadding: false, label: t('datatable.registrationDate') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.address') },
  { id: "cp", numeric: false, disablePadding: false, label: t('datatable.postalCode') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.municipality') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.services') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.employees') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.riskIdentification') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.emergencyPlan') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.preventionPlan') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.visitSheet') },
  { id: "location", numeric: false, disablePadding: false, label: t('datatable.notVisited') },
];

const EnhancedTableHead = ({
  onSelectAllClick,
  setAllSelected,
  allSelected,
  selected,
  data,
  headersData,
  order,
  orderBy,
  onRequestSort,
  enableSelection,
  enumerate,
  segundaCol,
  propCaducada
}) => {
  const { t } = useTranslation('common');
  const headCells = getDefaultHeadCells(t);
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const setHeaders = (headers) => {
    return headers.map((headCell) => (
      <TableCell
        key={headCell.id}
        align={headCell.numeric ? "right" : "left"}
        padding={headCell.disablePadding ? "none" : "normal"}
        sortDirection={orderBy === headCell.id ? order : false}
        size="small"
        noObjeto={headCell.noObjeto}
      >
        {headCell.noObjeto !== true ? (
          <TableSortLabel
            active={orderBy === headCell.id}
            direction={orderBy === headCell.id ? order : "asc"}
            onClick={createSortHandler(headCell.id)}
            sx={{
              color: "white !important",
              "&:hover": { color: "white" },
              "&:active": { color: "white" },
              svg: { filter: "invert(1) brightness(1)" },
              fontSize: "0.8rem",
            }}
          >
            {headCell.label}
            {orderBy === headCell.id ? (
              <Box component="span" sx={visuallyHidden}>
                {order === "desc" ? "sorted descending" : "sorted ascending"}
              </Box>
            ) : null}
          </TableSortLabel>
        ) :
          (
            <Typography
              sx={{
                color: "white !important",
                fontSize: "0.8rem",
              }}
            >
              {headCell.label}
            </Typography>
          )}
      </TableCell>
    ));
  };

  return (
    <TableHead sx={{ backgroundColor: "#92004D" }}>
      <TableRow

      >
        {enableSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              sx={{
                color: '#fff',
                '&.Mui-checked': { color: '#fff' },
                '&.MuiCheckbox-indeterminate': { color: '#fff' }
              }}
              color="primary"
              name="selectAll"
              indeterminate={allSelected}
              checked={allSelected}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all names",
              }}
            />
          </TableCell>
        )}
        {segundaCol && (
          <TableCell>
            <Typography></Typography>
          </TableCell>
        )}
        {propCaducada && (
          <TableCell>
            <Typography></Typography>
          </TableCell>
        )}
        {enumerate && (
          <TableCell>
          </TableCell>
        )}
        {headersData ? setHeaders(headersData) : setHeaders(headCells)}
      </TableRow>
    </TableHead>
  );
};

const LegaliaSortIcon = ({ active, direction }) => (
  <svg className="w-3.5 h-3.5 text-gray-400 inline-block ml-1" viewBox="0 0 16 16" fill="currentColor">
    {active ? (
      direction === "asc" ? (
        <path d="M8 3l3 3H5l3-3z" />
      ) : (
        <path d="M8 13l-3-3h6l-3 3z" />
      )
    ) : (
      <path d="M8 3l3 3H5l3-3zm0 10l-3-3h6l-3 3z" />
    )}
  </svg>
);

// Elementos que gestionan su propio click y no deben abrir el detalle de la fila
const SELECTOR_CONTROLES = 'a, button, input, select, textarea, label, svg, img, [role="button"], .tableIcon, .btn, .MuiIconButton-root, .MuiCheckbox-root';

const DataTable = ({
  data,
  meta,
  headers,
  isLoading,
  onClickRow,
  fetcher,
  enableSection,
  children,
  filtros,
  busquedaActiva,
  setBusquedaActiva,
  clearFilterActivo,
  setClearFilterActivo,
  recargar,
  mensaje,
  clicable,
  enumerate = false,
  segundaCol,
  propCaducada,
  setSelected,
  selected,
  allSelected,
  setAllSelected,
  variant,
  renderExpandedRow,
  viewMode = "table",
  renderCard,
  resumenPie,
}) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  let params = new URLSearchParams(location.search);
  const pagina = parseInt(params.get("pagina")) || 1;
  const limiteFromUrl = parseInt(params.get("limite"));
  const defaultRowsForMode = 50;
  const limiteParam = limiteFromUrl || defaultRowsForMode;

  // const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(pagina);
  const [rowsPerPage, setRowsPerPage] = useState(limiteParam);
  const prevViewMode = useRef(viewMode);

  useEffect(() => {
    if (prevViewMode.current !== viewMode) {
      prevViewMode.current = viewMode;
      setPage(1);
      setRowsPerPage(50);
    }
  }, [viewMode]);

  const rowsPerPageOptions = viewMode === "cards"
    ? [10, 25, 50, 100, 150, 200, 250, 300]
    : [50, 100, 150, 200, 250, 300];
  const [isArrowDown, setIsArrowDown] = useState(false);
  const [filteredData, setFilteredData] = useState(data);
  const [expanded, setExpanded] = useState(null);
  const { filtroOrder } = useRecogerURLParamsFiltros();

  const [order, setOrder] = useState(filtroOrder.orden ? filtroOrder.orden.toLowerCase() : '');
  const [orderBy, setOrderBy] = useState(filtroOrder.ordenadoPor);
  const [orderApi, setOrderApi] = useState(filtroOrder.orden);

  // const handleTabFilterValue = (event, newValue) => {
  //   const newData = data.filter(
  //     (row) => row[newValue] !== null || row[newValue] !== "false"
  //   );
  //   setFilteredData(newData);
  // };

  // const handleStatusClick = () => {
  //   setIsArrowDown((prevIsArrowDown) => !prevIsArrowDown);

  //   const newData = isArrowDown
  //     ? data?.filter((row) => row.status === "0")
  //     : data?.filter((row) => row.status === "1");

  //   setFilteredData(newData);

  // };
  const handleSelectAllClick = (event) => {
    console.log(event.target.name);
    if (event.target.checked) {
      setAllSelected(true);
      
      setSelected([]); // Limpiamos selecciones individuales
    } else {
      setAllSelected(false);
      setSelected([]);
    }
  };
  const handleIndividualCheck = (rowId) => {
    // Si allSelected está activo, lo desactivamos al seleccionar individualmente
    if (allSelected) {
      setAllSelected(false);
      // Seleccionamos todos excepto el que se está deseleccionando
      const newSelected = data.map(row => row.id).filter(id => id !== rowId);
      setSelected(newSelected);
    } else {
      // Lógica normal de selección individual
      const selectedIndex = selected.indexOf(rowId);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, rowId);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }
      setSelected(newSelected);
    }
  };
  const handleNext = () => {
    if (setBusquedaActiva && typeof setBusquedaActiva === 'function') {
      setBusquedaActiva(false);
    }
    setPage((page) => page + 1);
  };

  const handleBack = () => setPage((page) => (page === 1 ? page : page - 1));

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderApi(isAsc ? "DESC" : "ASC");
    setOrderBy(property);
  };

  // const visibleRows = useMemo(
  //   () => stableSort(data, getComparator(order, orderBy)),
  //   [order, orderBy, data]
  // );

  useEffect(() => {
    if (busquedaActiva || clearFilterActivo) {
      setPage(1);
      fetcher({ offset: 1, limit: rowsPerPage, filtros: filtros, orderBy: orderBy, order: orderApi });
      if (setBusquedaActiva && typeof setBusquedaActiva === "function") {
        setBusquedaActiva(false);
      }
      if (setClearFilterActivo && typeof setClearFilterActivo === "function") {
        setClearFilterActivo(false);
      }
    } else {

      fetcher({ offset: page, limit: rowsPerPage, filtros: filtros, orderBy: orderBy, order: orderApi });
      // fetcher({ offset: page, limit: rowsPerPage, filtros: filtros, orderBy: orderBy ,order: order });
    }
  }, [page, rowsPerPage, recargar, filtros, orderApi, orderBy, busquedaActiva, clearFilterActivo]);

  useEffect(() => {
    setPage(pagina)
    //fetcher({ offset: page, limit: rowsPerPage, filtros: filtros, orderBy: orderBy ,order: order });
  }, [pagina]);

  const visibleRows = data;


  const setTabs = (tabs) =>
    tabs.map((tab) => <Tab value={tab.value} label={tab.label} />);

  const isLastPage = !meta?.has_more_pages;

  const PaginationBar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          gap: 3,
          alignItems: "center",
        }}
      >
        <Box className="lae-flex" sx={{ alignItems: "center", marginLeft: 1 }}>
          <Typography>{t('table.rowsPerPage')}:</Typography>
          <Select
            value={rowsPerPage}
            label="Age"
            size="small"
            variant="standard"
            onChange={({ target }) => {
              setPage(1);
              setRowsPerPage(target.value);
            }}
          >
            {[50, 100, 150, 200, 250, 300].map((option) => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            alignItems: "center",
          }}
        >
          {resumenPie ? (
            <Box className="lae-flex lae-flex-row">
              <Typography>{resumenPie}</Typography>
            </Box>
          ) : null}

          <Box className="lae-flex lae-flex-row">
            <Typography>
              {t('table.recordsOf', { current: meta && Object.keys(meta).length > 0 ? (isLastPage ? meta.total : meta.per_page * page) : 0, total: meta?.total ?? 0 })}
            </Typography>
          </Box>

          <Box className="lae-flex" sx={{
            gap: 0,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <Button variant="text" style={{ minWidth: 0 }} onClick={handleBack} disabled={page === 1}>
              <Iconify
                icon="material-symbols:chevron-left"
                sx={{
                  color: page === 1 ? "gray" : "black",
                  height: 24,
                  width: 24,
                }}
              />
            </Button>
            <Typography>{meta && Object.keys(meta).length > 0 ? page : 0} / {meta?.last_page || 0}</Typography>
            <Button variant="text" style={{ minWidth: 0 }} onClick={handleNext} disabled={isLastPage}>
              <Iconify
                icon="material-symbols:chevron-right"
                sx={{
                  color: isLastPage ? "gray" : "black",
                  height: 24,
                  width: 24,
                }}
              />
            </Button>
            <style jsx>{`
          .css-1e6y48t-MuiButtonBase-root-MuiButton-root.Mui-disabled{
            opacity: 0.5
          }
        `}</style>
          </Box>
        </Box>
      </Box>
    )
  }

  // paginacion de numeros para legalia
  const handlePageChange = (newPage) => {
    if (setBusquedaActiva && typeof setBusquedaActiva === 'function') {
      setBusquedaActiva(false);
    }
    setPage(newPage);
  };

  const totalPages = meta?.last_page || 1;

  const pageNumbers = () => {
    const nums = [];
    const tp = totalPages;
    const cp = page;
    if (tp <= 7) {
      for (let i = 1; i <= tp; i++) nums.push(i);
    } else {
      if (cp <= 4) {
        for (let i = 1; i <= 5; i++) nums.push(i);
        nums.push("...", tp);
      } else if (cp >= tp - 3) {
        nums.push(1, "...");
        for (let i = tp - 4; i <= tp; i++) nums.push(i);
      } else {
        nums.push(1, "...", cp - 1, cp, cp + 1, "...", tp);
      }
    }
    return nums;
  };

  if (variant === "legalia") {
    return (
      <div className="w-full">
        {children}

        {isLoading ? (
          <div className="flex justify-center py-10">
            <CircularProgress style={{ height: 20, width: 20, color: "#92004D" }} />
          </div>
        ) : visibleRows?.length === 0 ? (
          <div className="flex justify-center py-10">
            <div className="text-gray-400">{mensaje}</div>
          </div>
        ) : viewMode === "cards" && typeof renderCard === "function" ? (
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}
          >
            {visibleRows.map((row, index) => {
              const rowKey = row.id || row.nif || index;
              return (
                <div key={rowKey} className="h-full">
                  {renderCard(row, index)}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-3 sm:mx-0"><table className="w-full text-sm min-w-[800px]" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr className="border-b border-gray-200">
                {enumerate && <th className="w-10" />}
                {headers?.map((col) => (
                  <th
                    key={col.id}
                    className="text-left text-xs font-medium text-gray-500 pb-3 pr-4 cursor-pointer select-none whitespace-nowrap"
                    onClick={() => {
                      if (col.noObjeto !== true) handleRequestSort(null, col.id);
                    }}
                  >
                    {col.label}
                    {col.noObjeto !== true && (
                      <LegaliaSortIcon
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                      />
                    )}
                  </th>
                ))}
                {renderExpandedRow && <th className="w-10" />}
              </tr>
            </thead>
            <tbody>
              {visibleRows?.map((row, index) => {
                const rowKey = row.id || row.nif || index;
                const isExpanded = expanded === rowKey;
                const colCount = (enumerate ? 1 : 0) + (headers?.length || 0) + (renderExpandedRow ? 1 : 0);
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`border-b border-gray-200 hover:bg-gray-50/60 transition-colors ${
                        renderExpandedRow ? "cursor-pointer" : clicable ? "cursor-pointer" : ""
                      }`}
                      onClick={() => {
                        if (renderExpandedRow) {
                          setExpanded(isExpanded ? null : rowKey);
                        } else if (clicable && onClickRow) {
                          onClickRow(row);
                        }
                      }}
                    >
                      {enumerate && (
                        <td className="py-3.5 pr-2 text-gray-400 text-xs">
                          {row.isWorkcenter
                            ? ""
                            : (page - 1) * rowsPerPage +
                              visibleRows.slice(0, index + 1).filter((r) => !r.isWorkcenter).length}
                        </td>
                      )}
                      {headers?.map((header) => (
                        <td key={header.id} className="py-3.5 pr-4 text-gray-600">
                          {typeof header?.overrideFunc === "function"
                            ? header.overrideFunc(row[header.id], row)
                            : row[header.id] || ""}
                        </td>
                      ))}
                      {renderExpandedRow && (
                        <td className="py-3.5 text-center">
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform inline-block ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </td>
                      )}
                    </tr>
                    {renderExpandedRow && isExpanded && (
                      <tr className="bg-gray-50/40">
                        <td colSpan={colCount} className="text-sm text-gray-600 py-3 px-4">
                          {renderExpandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}

        {/* Legalia Pagination */}
        {visibleRows?.length > 0 && meta && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-3 text-sm text-gray-900">
            <div className="flex flex-wrap items-center gap-2">
              <span>
                {t("table.showing")}{" "}
                {meta.total > 0
                  ? `${(page - 1) * (meta.per_page || rowsPerPage) + 1}-${Math.min(
                      page * (meta.per_page || rowsPerPage),
                      meta.total
                    )}`
                  : "0"}{" "}
                {t("table.of")} {meta.total || 0}
              </span>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={({ target }) => {
                    setPage(1);
                    setRowsPerPage(Number(target.value));
                  }}
                  className="appearance-none pl-2 pr-6 py-0.5 text-sm border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {rowsPerPageOptions.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <svg
                  className="w-3 h-3 text-gray-400 absolute right-1.5 top-1/2 pointer-events-none"
                  style={{ transform: "translateY(-50%)" }}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1">
              <button
                onClick={handleBack}
                disabled={page === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
                {/* <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 010 1.06L8.06 10l3.72 3.72a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z" clipRule="evenodd" />
                </svg> */}
              </button>
              {pageNumbers().map((n, i) =>
                n === "..." ? (
                  <span key={`ellipsis-${i}`} className="px-1">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => handlePageChange(n)}
                    className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                      page === n
                        ? "text-[#92004D]"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                    style={page === n ? { border: "1px solid #92004D" } : {}}
                  >
                    {n}
                  </button>
                )
              )}
              <button
                onClick={handleNext}
                disabled={isLastPage}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              >
                {/* <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 010-1.06z" clipRule="evenodd" />
                </svg> */}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <PaginationBar />
        <TableContainer>
          {children}
          {isLoading && <LinearProgress sx={{ width: "100%" }} />}
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              selected={selected}
              setAllSelected={setAllSelected}
              onSelectAllClick={handleSelectAllClick}
              allSelected={allSelected}
              order={order}
              orderBy={orderBy}
              data={filteredData}
              isArrowDown={isArrowDown}
              // onStatusClick={handleStatusClick}
              headersData={headers}
              enableSelection={enableSection}
              enumerate={enumerate}
              onRequestSort={handleRequestSort}
              segundaCol={segundaCol}
              propCaducada={propCaducada}

            />

            <TableBody>
              {isLoading ? (
                <TableRow hover sx={{ cursor: "default" }}>
                  <TableCell colSpan={18} style={{ textAlign: "center" }}>
                    <CircularProgress style={{ color: "#92004D" }} />
                  </TableCell>
                </TableRow>
              ) : visibleRows?.length === 0 ? (
                <TableRow hover sx={{ cursor: "default" }}>
                  <TableCell colSpan={18} style={{ textAlign: "center" }}>
                    <span>{mensaje}</span>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows?.length &&
                visibleRows.map((row, index) => {
                  const isItemSelected = allSelected || selected?.indexOf(row.id) !== -1;
                  return (
                    <TableRow
                      hover key={row.id}
                      onClick={(e) => {
                        if (!clicable || typeof onClickRow !== "function") return;
                        // Un click sobre un enlace o un boton hace lo suyo, no abre el detalle
                        if (e.target.closest(SELECTOR_CONTROLES)) return;
                        onClickRow(row);
                      }}
                      sx={{ cursor: clicable ? "pointer" : "default" }}
                      style={row.impagada ? { backgroundColor: "#fdc0c0" } : undefined}
                      className={[row.caducada ? "caducada" : "", row.isWorkcenter ? index % 2 === 0 ? 'table-row-pair' : 'table-row-odd' : ""]}>
                      {enableSection && (
                        <TableCell padding="checkbox" colSpan={row.colSpan}>
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={() => handleIndividualCheck(row.id)}
                            
                          /*  inputProps={{
                          "aria-labelledby": labelId,
                        }} */
                          />
                        </TableCell>
                      )}

                      {segundaCol && (
                        <TableCell style={{padding:0}}>
                          <p style={{
                            ...(row.esCreatedAt ? { width: '3px', height: '7vh', background: '#0767A2' } : {})
                          }}>
                          </p>
                        </TableCell>
                      )}
                      {propCaducada && (
                        <TableCell style={{padding:0}}>
                          <p style={{
                            ...(row.caducada ? { width: '3px', height: '5vh', background: '#EE4534' } : {}) 
                          }}>
                          </p>
                        </TableCell>
                      )}
                      {enumerate && (
                        <TableCell padding="checkbox" colSpan={row.colSpan}>
                          {row.isWorkcenter
                            ? ""
                            : (page - 1) * rowsPerPage +
                              visibleRows.slice(0, index + 1).filter((r) => !r.isWorkcenter).length}
                        </TableCell>
                      )}
                      {headers?.map((header) => {

                        return (
                          <TableCell
                            align={header?.numeric ? "right" : "left"}
                            style={header?.style}
                            key={header.id}
                          >
                            {typeof header?.overrideFunc === "function"
                              ? header?.overrideFunc(row[header.id], row)
                              : row[header.id] || ""}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationBar />
      </Paper>
    </Box>
  );
};

export default DataTable;
